import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { ProductForm } from "../components/ProductForm";
import { http, errMsg, fetchProducts } from "../lib/api";
import { formatPrice } from "../lib/i18n";

const Row = ({ p, onEdit, onDelete }) => (
  <tr data-testid={`admin-row-${p.id}`} className="border-b border-zinc-200">
    <td className="py-3 pr-4"><img src={p.images?.[0]} alt="" className="w-12 h-16 object-cover bg-zinc-200" /></td>
    <td className="py-3 pr-4"><p className="font-medium">{p.name_tr}</p><p className="text-xs text-zinc-500">{p.name_en}</p></td>
    <td className="py-3 pr-4"><span className={`px-2 py-1 f-mono text-[11px] uppercase ${p.world === "street" ? "bg-zinc-900 text-[var(--acid)]" : "bg-[#efe6d4] text-[var(--navy)]"}`}>{p.world === "street" ? "Street" : "Old Money"}</span></td>
    <td className="py-3 pr-4 f-mono text-sm">{formatPrice(p.price)}</td>
    <td className="py-3 text-right whitespace-nowrap">
      <button data-testid={`admin-edit-${p.id}`} onClick={() => onEdit(p)} className="p-2 hover:bg-zinc-200 transition-colors"><Pencil size={15} /></button>
      <button data-testid={`admin-delete-${p.id}`} onClick={() => onDelete(p)} className="p-2 hover:bg-red-100 text-red-600 transition-colors"><Trash2 size={15} /></button>
    </td>
  </tr>
);

export default function Admin() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => { fetchProducts().then(setItems); }, []);
  useEffect(() => {
    http.get("/auth/me").then((r) => { setUser(r.data); load(); }).catch(() => nav("/admin/login"));
  }, [nav, load]);

  const save = async (body) => {
    try {
      if (editing?.id) await http.put(`/products/${editing.id}`, body);
      else await http.post("/products", body);
      toast("Kaydedildi");
      setOpen(false);
      load();
    } catch (e) { toast(errMsg(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`${p.name_tr} silinsin mi?`)) return;
    try { await http.delete(`/products/${p.id}`); toast("Silindi"); load(); } catch (e) { toast(errMsg(e)); }
  };
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); nav("/admin/login"); };

  if (!user) return <main className="min-h-screen bg-[#faf7f1]" />;
  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#faf7f1] text-zinc-900">
      <header className="flex items-center justify-between px-5 md:px-10 py-5 border-b border-zinc-200">
        <Link to="/" className="flex items-center gap-3"><LogoMark /><span className="f-wordmark tracking-[0.4em] text-sm">ANVOCRA</span></Link>
        <div className="flex items-center gap-4 f-mono text-[12px]">
          <span className="hidden sm:inline text-zinc-600">{user.email}</span>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1 hover:underline"><LogOut size={14} /> Çıkış</button>
        </div>
      </header>
      <section className="px-5 md:px-10 py-10">
        <div className="flex items-end justify-between mb-8">
          <h1 className="f-serif italic text-5xl">Ürünler <span className="f-mono not-italic text-sm text-zinc-500">({items.length})</span></h1>
          <button data-testid="admin-add-product-btn" onClick={() => { setEditing(null); setOpen(true); }} className="flex items-center gap-2 h-11 px-5 rounded-full bg-zinc-900 text-white f-mono text-[12px] uppercase tracking-[0.2em]"><Plus size={14} /> Ekle</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[640px]">
            <tbody>{items.map((p) => <Row key={p.id} p={p} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={remove} />)}</tbody>
          </table>
        </div>
      </section>
      <ProductForm open={open} onOpenChange={setOpen} initial={editing} onSave={save} />
    </main>
  );
}
